<template>
  <div class="bom__container landscape">

    <div class="bom__info">

      <button 
        class="noprint button is-link is-fullwidth mb-5" 
        @click.stop="editing = true">
        <span class="icon is-medium">
          <i class="fas fa-save"></i>
        </span>
        <span>              
          Edit Bom
        </span>
      </button>

      <div v-if="!loading" class="bom-info">
        <div class="field">
          <label class="label">Project</label>
          <div>{{ bom.project.project_name }}</div>
        </div>
        
        <div class="field">
          <label class="label">BOM Name / Desc.</label>
          <v-text-field 
            class="noprint"
            :disabled="!editing"
            required
            v-model="bom.name"/>
          <span class="printonly">
            {{ bom.name }}
          </span>
        </div>

        <div class="field">
          <label class="label">Created On</label>
          <span>
            {{ formatDate(parseDate(bom.created_on)) }}
          </span>
        </div>

        <div class="field">
          <label class="label">Created By</label>
          <span>
            {{ bom.created_user.display_name }}
          </span>
        </div>
      </div>

    </div>

    <div class="bom__details">

      <v-fade-transition mode="out-in">
        <div class="level" v-if="editing">
          <div class="level-left buttons mb-0">
            <a class="button is-primary" @click="adding = true">
              Add a Bom Detail
            </a>
            <a 
              v-if="!loading"
              class="button is-primary" 
              @click="addingSlabItems = true">
              Add Existing Slab Items to BOM
            </a>
          </div>

          <div class="level-right buttons mb-0">
            <a class="button" 
              v-if="selected.length > 0"
              @click="deleteSelected()">
              <span class="icon is-medium">
                <i class="fas fa-trash has-text-danger"></i>
              </span>
              <span>Delete {{ selected.length }} Detail<span v-if="selected.length > 0">s</span></span>
            </a>
          </div>
        </div>
      </v-fade-transition>

      <article 
        v-if="!loading && bom.details.length == 0" 
        class="message is-info is-fullwidth">
        <div class="message-body">
          There are no details for this BOM.
        </div>
      </article>

      <div v-if="!loading && slabItems.length > 0" class="mb-5">
        <header class="menu-label is-5">
          Slab Items
        </header>

        <table class="table is-fullwidth is-hoverable has-small-font">
          <thead>
            <tr>
              <th 
                v-for="(header, headerIndex) in headers" 
                :key="headerIndex"
                :class="header.class">
                {{ header.label }}
              </th>
              <th v-if="editing" class="noprint"></th>
            </tr>
          </thead>
          <tbody>
            <tr 
              class="v-centered"
              v-for="item in slabItems"
              :key="item.id">
              <td>
                {{ item.product_master ? 
                  item.product_master.product_category ? 
                  item.product_master.product_category.category : '' : '' }}
              </td>
              <td>
                {{ item.product_master ? item.product_master.name : '' }}
              </td>
              <td>
                <template v-if="item.product_master">
                  <span v-if="item.product_master.product_category.has_dimensions">
                    L{{ item.stocking_unit.length_in_str }}" x 
                    W{{ item.stocking_unit.length_in_str }}" x 
                    T{{ item.stocking_unit.thick_in_str }}"
                  </span>
                  <span v-else>
                    {{ item.product_master.size }}
                  </span>
                </template>
              </td>
              <td>
                {{ item.product_master ? item.product_master.desc : '' }}
              </td>
              <td class="noprint">
                <a 
                  v-if="item.product_master && item.product_master.image"
                  @click="preview.src = item.product_master.image; preview.state = true">
                  Product Sample Image
                </a>
                <span v-else class="has-text-grey">
                  No sample image
                </span>
              </td>
              <td>
                <span v-if="item.id != editingId">
                  {{ item.finished_qty }}
                </span>
                <v-text-field 
                  v-else
                  v-model="item.finished_qty">
                </v-text-field>
              </td>
              <td>
                {{ item.product_master ? item.product_master.unit ? 
                  item.product_master.unit.unit_display_name : '' : '' }}
              </td>
              <td>
                <v-styled-checkbox 
                  disabled
                  :value="item.is_ordered"/>
              </td>
              <td>
                <v-styled-checkbox 
                  disabled
                  :value="item.is_delivered"/>
              </td>
              <td class="noprint" v-if="editing">
                <div class="buttons buttons-right">
                  <button  
                    @click="deleteId = item.id; launchConfirmDeleteSlab()"
                    class="button is-small">
                    <span class="has-text-grey icon">
                      <i class="fas fa-trash"></i>
                    </span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <header 
        v-if="!loading && bom.details.length - slabItems.length > 0" 
        class="menu-label is-5">
        Other Items
      </header>

      <table 
        v-if="!loading && bom.details.length - slabItems.length > 0" 
        class="table is-hoverable is-fullwidth has-small-font">

        <thead>
          <tr>
            <th v-if="editing" class="noprint">
              <v-checkbox
                :value="allSelected"
                @input="toggleAllSelected($event)">
              </v-checkbox>
            </th>
            <th
              v-for="(header, headerIndex) in headers" 
              :key="headerIndex"
              :class="header.class">
              {{ header.label }}
            </th>
            <th v-if="editing"></th>
          </tr>
        </thead>

        <tbody>
          <template 
            v-for="detail in bom.details">
            <tr 
              v-if="!slabItemIds.includes(detail.id)"
              class="v-centered"
              :key="detail.id">
              <td v-if="editing">
                <v-checkbox
                  :value="selected.indexOf(detail.id) != -1"
                  @input="toggleSelected(detail.id)">
                </v-checkbox>
              </td>
              <td>
                {{ detail.product_master ? 
                  detail.product_master.product_category.category : '' }}
              </td>
              <td>
                {{ detail.product_master ? detail.product_master.name : '' }}
              </td>
              <td>
                <template v-if="detail.product_master">
                  <span v-if="detail.product_master.product_category.has_dimensions">
                    L{{ detail.stocking_unit.length_in_str }}" x W{{ detail.stocking_unit.length_in_str }}" x T{{ detail.stocking_unit.thick_in_str }}"
                  </span>
                  <span v-else>
                    {{ detail.product_master.size }}
                  </span>
                </template>
              </td>
              <td>
                {{ detail.product_master ? detail.product_master.desc : '' }}
              </td>
              <td class="noprint">
                <template v-if="detail.product_master">
                  <a 
                    v-if="detail.product_master.image"
                    @click="preview.src = detail.product_master.image; preview.state = true">
                    Product Sample Image
                  </a>
                  <span v-else class="has-text-grey">
                    No sample image
                  </span>
                </template>
              </td>
              <td>
                <span v-if="detail.id != editingId">
                  {{ detail.finished_qty }}
                </span>
                <v-text-field 
                  v-else
                  v-model="detail.finished_qty">
                </v-text-field>
              </td>
              <td>
                {{ detail.product_master ? detail.product_master.unit ? 
                  detail.product_master.unit.unit_display_name : '' : '' }}
              </td>
              <td>
                <v-styled-checkbox 
                  disabled
                  :value="detail.is_ordered"/>
              </td>
              <td>
                <v-styled-checkbox 
                  disabled
                  :value="detail.is_delivered"/>
              </td>
              <td v-if="editing">
                <div class="buttons buttons-right">
                  <template v-if="detail.id != editingId">
                    <button
                      @click="editingId = detail.id"
                      class="button is-small">
                      <span class="has-text-grey icon">
                        <i class="fas fa-edit"></i>
                      </span>
                    </button>
                    <button  
                      @click="deleteId = detail.id; launchConfirmDelete()"
                      class="button is-small">
                      <span class="has-text-grey icon">
                        <i class="fas fa-trash"></i>
                      </span>
                    </button>
                  </template>
                  <template v-else>
                    <button  
                      @click="saveEdits(detail)"
                      class="button is-small is-primary">
                      <span class="icon">
                        <i class="fas fa-check"></i>
                      </span>
                    </button>
                  </template>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmed()"
      @cancel="deleteId = null; confirmDelete.state = false"/>

    <add-bom-detail-modal
      :bom-id="$route.params.bomId" 
      v-model="adding"/>

    <v-file-preview 
      v-model="preview.state"
      :src="preview.src"/>

    <add-slabs-to-bom-editor-modal
      v-if="!loading" 
      v-model="addingSlabItems"/>

  </div>
</template>

<script>
// import VForm from '@/components/base/VForm'
// import VSelect from '@/components/base/VSelect'
import VConfirmModal from '@/components/base/VConfirmModal'
import VTextField from '@/components/base/VTextField'
import VCheckbox from '@/components/base/VCheckbox'
import VStyledCheckbox from '@/components/base/VStyledCheckbox'
import VFilePreview from '@/components/base/VFilePreview'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import AddBomDetailModal from '@/components/inventory/AddBomDetailModal'
import AddSlabsToBomEditorModal from '@/components/inventory/AddSlabsToBomEditorModal'

import { mapState, mapActions, mapMutations } from 'vuex'

import { parseISO, format } from 'date-fns'

export default {
  components: { 
    VConfirmModal,
    VTextField,
    VCheckbox,
    VStyledCheckbox,
    VFilePreview,
    VFadeTransition,
    AddBomDetailModal,
    AddSlabsToBomEditorModal
  },
  data() {
    return {
      editing: false,
      editingId: null,
      adding: false,
      headers: [
        { label: 'Product Category' },
        { label: 'Product' },
        { label: 'Product Size' },
        { label: 'Product Specs' },
        { label: 'Product Sample Image', class: 'noprint' },
        { label: 'Finished Quantity' },
        { label: 'Product Unit' },
        { label: 'Ordered' },
        { label: 'Delivered' }
      ],
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this BOM Detail?',
        type: 'danger'
      },
      deleteId: null,
      slabDelete: false,
      bulk: false,
      preview: {
        state: false,
        src: null
      },
      addingSlabItems: false,
      parseDate: (d) => {
        return d ? parseISO(d) : null;
      },
      formatDate: (d) => {
        return d ? format(d, 'yyyy-MM-dd H:mm:ss') : d
      }
    }
  },
  computed: {
    ...mapState("bom", [
      "bom", "selected", "allSelected", "loading"
    ]),
    slabItems() {
      return this.bom.details.filter(d => 
        d.production_sheet_item_id != null
      )
    },
    slabItemIds() {
      return this.slabItems.map(d => d.id);
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("bom", [
      "getBom", "deleteDetail", "updateDetail", "deleteSlabItem",
      "toggleAllSelected"
    ]),
    ...mapMutations("bom", ["setBom", "deselectAll", "toggleSelected"]),
    initialize() {
      if (this.$route.params.bom) {
        this.setBom(this.$route.params.bom);
      } else {
        this.getBom(this.$route.params.bomId);
      }
    },
    saveEdits(detail) {
      this.updateDetail({ 
        detailId: detail.id, 
        data: { 
          finished_qty: detail.finished_qty,
          qty_to_be_ordered: detail.qty_to_be_ordered 
        }
      }).then(
        () => this.editingId = null
      );
    },
    launchConfirmDelete(bulk=false) {
      this.bulk = bulk;
      if (bulk) {
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} BOMs?`;
      } else {
        this.confirmDelete.message = 'Are you sure you want to delete this BOM?'
      }
      this.confirmDelete.state = true;
    },
    launchConfirmDeleteSlab() {
      this.confirmDelete.message = 'Are you sure you want to delete this slab item? \
      Deleting one slab item will delete the facing, backing, and panel from the BOM'
      this.confirmDelete.state = true;
      this.slabDelete = true;
    },
    deleteConfirmed() {
      this.confirmDelete.state = false;

      if (this.bulk == true) {
        this.deleteSelected();
      } else {
        if (!this.slabDelete) {
          this.deleteDetail(this.deleteId).then(
            () => {
              this.deleteId = null;
              if (this.selectedIndexOf(this.deleteId) != -1) {
                this.toggleSelected(this.deleteId);
              }
            }
          );
        } else {
          let prodSheetItemId = this.bom.details.filter(
            d => d.id == this.deleteId
          )[0].production_sheet_item_id;

          this.deleteSlabItem({ 
            bomDetailId: this.deleteId, 
            prodSheetItemId: prodSheetItemId 
          }).then(
            () => {
              this.deleteId = null;
            }
          )
        }
      }
    },
    deleteSelected() {
      this.selected.forEach(bomId => {
        this.deleteDetail(bomId).then(
          () => this.deselectAll()
        )
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/printable.scss';
@import '@/styles/table.scss';

.bom {
  &__container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }
  &__info {
    padding: 1.5em;
    width: 20%;
  }
  &__details {
    padding-right: 1.5em;
    padding-top: 1.5em;
    padding-bottom: 1.5em;
    width: 80%;
  }
}

@media print {
  .bom {
    &__info {
      width: 100%;
      padding: 0em;
      margin: 0em -0.8em;
    }
    &__details {
      width: 100%;
      padding-right: 0em;
      padding-bottom: 0em;
    }
  }
  .table {
    font-size: 0.75em;
  }
  .bom-info {
    display: flex;
    flex-direction: row;
    .field {
      margin: 0em 1em;
      font-size: 0.8em;
      .label {
        font-size: 0.9em;
      }
    }
  }
}
</style>